import { useDebouncedCallback } from '@mantine/hooks';
import {
  ContentCategory,
  DigestData,
  DigestJobResult,
  ReportChartCXMetricMeasureApiType,
  ReportChartDataRequestItem,
  ReportChartGroupCompareItemType,
  ReportChartGroupItemType,
  ReportChartJobIdResponse,
  ReportChartMeasureType,
  ReportFeedbackCxMetricsResponse,
  ReportFeedbackNameType,
  ReportFeedbackResponse,
  ReportFeedbackResponseArrayDataType,
  ReportFeedbackResponseCollection,
  ReportFeedbackStaticResponseItem,
  ReportFeedbackData,
  ReportDataSource,
} from '@vision/ui/interfaces';
import { useCreateFeedbackOrJobIdMutation, useGetFeedbackStaticResultQuery } from '@vision/ui/services';
import { useJobResults, useReportDataRequestQueue } from '@vision/ui/context';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getReportChartCompareAxisValue,
  getReportDataName,
  isReportFeedbackResponseArrayDataTypeArray,
  isReportFeedbackResponseArrayDataTypeNumber,
  isReportReportFeedbackResponse,
  calculateTotalFeedbackCount,
} from '../pages/ReportsDetailGraphic/utils';
import { selectContentCategoriesSelector } from '../store';
import {
  CachedChartResponse,
  ENV,
  getCachedChartResponse,
  parseReportPrintParams,
  reportWidgetHasContentCategoryCompare,
  getDigestDataFromUrl,
} from '../utils';
import { useAppSelector } from './useAppSelector';
import { getDisplayProcessItems, localizeTicketMeasureNames } from './useReportFeedbackStatic.utils';

export function isReportChartJobIdResponse(obj: any): obj is ReportChartJobIdResponse {
  return isObjectWithProperty(obj, 'job_id');
}

export function isReportFeedbackData(obj: any): obj is { data: ReportFeedbackResponseCollection } {
  return isObjectWithProperty(obj, 'data');
}

function isObjectWithProperty(obj: unknown, property: string): boolean {
  return obj && typeof obj === 'object' && property in obj;
}

function getTotalFeedbackCount(value: unknown): number | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const totalFeedbackCount = (value as { total_feedback_count?: number }).total_feedback_count;
  if (typeof totalFeedbackCount === 'number') {
    return totalFeedbackCount;
  }

  const nestedData = (value as { data?: unknown }).data;
  if (!nestedData || typeof nestedData !== 'object') {
    return undefined;
  }

  const nestedTotalFeedbackCount = (nestedData as { total_feedback_count?: number }).total_feedback_count;

  return typeof nestedTotalFeedbackCount === 'number' ? nestedTotalFeedbackCount : undefined;
}

function updateCategoryContentNames(
  data: ReportFeedbackResponseCollection,
  contentCategories: ContentCategory[],
  widgetType: ReportChartGroupItemType,
  compares: ReportChartGroupCompareItemType[],
): ReportFeedbackResponseCollection {
  if (widgetType === 'bar-chart-3') {
    const typedData = data as ReportFeedbackCxMetricsResponse;
    return Object.keys(typedData).reduce((acc, key) => {
      const typedKey = key as ReportChartCXMetricMeasureApiType;
      if (!Array.isArray(typedData[typedKey])) {
        return acc;
      }

      acc[typedKey] = typedData[typedKey].map((item) => {
        const contentCategory = contentCategories.find((contentCategory) => contentCategory.path === item.name);
        return {
          ...item,
          name: contentCategory?.name || item.name,
        };
      });
      return acc;
    }, {} as ReportFeedbackCxMetricsResponse);
  } else if (isReportFeedbackResponseArrayDataTypeArray(data)) {
    const typedData = data as ReportFeedbackResponseArrayDataType[];
    if (getReportChartCompareAxisValue(compares, 'x') === 'category-content-name') {
      return typedData.map((item) => {
        const contentCategory = contentCategories.find((contentCategory) => contentCategory.path === item.name);
        return {
          ...item,
          name: contentCategory?.name || item.name,
        };
      });
    } else {
      return typedData.map((item) => ({
        ...item,
        data: item.data.map((data: [string, number]) => {
          const contentCategory = contentCategories.find((contentCategory) => contentCategory.path === data[0]);
          return [getReportDataName(contentCategory?.name) || data[0], data[1]];
        }),
      }));
    }
  } else if (isReportReportFeedbackResponse(data)) {
    const typedData = data as ReportFeedbackResponse[];
    return typedData.map((item) => ({
      ...item,
      name: getReportDataName(item.name as ReportFeedbackNameType),
    }));
  } else if (isReportFeedbackResponseArrayDataTypeNumber(data)) {
    const typedData = data as ReportFeedbackResponse[];
    return typedData.map((item) => {
      const contentCategory = contentCategories.find((contentCategory) => contentCategory.path === item.name);
      return {
        ...item,
        name: contentCategory?.name || item.name,
        data: item.data,
      };
    });
  } else {
    return data;
  }
}

function isReportFeedbackCxMetricsResponse(
  data: ReportFeedbackResponseCollection,
): data is ReportFeedbackCxMetricsResponse {
  return !Array.isArray(data) && 'feedback_count' in data;
}

function calculateFeedbackIncrement(
  data?: ReportFeedbackResponseCollection,
  measure?: ReportChartMeasureType,
  isCountOnly: boolean = false,
): number {
  if (!data) {
    return 0;
  }

  if (isCountOnly) {
    return calculateTotalFeedbackCount(data);
  }

  if (measure === 'cx-metrics' && isReportFeedbackCxMetricsResponse(data)) {
    return calculateTotalFeedbackCount(data);
  }

  if (measure === 'feedback-count') {
    return calculateTotalFeedbackCount(data);
  }

  return 0;
}

export interface UseReportFeedbackStaticProps {
  widgetId?: string;
  widgetType?: ReportChartGroupItemType;
}

type ChartRequestResponse = CachedChartResponse;

function toChartRequestResponse({
  compare,
  measure,
  response,
}: {
  compare?: ReportChartGroupCompareItemType[];
  measure: ReportChartMeasureType;
  response: ReportChartJobIdResponse | { data: ReportFeedbackResponseCollection };
}): ChartRequestResponse {
  if (isReportChartJobIdResponse(response)) {
    return { compare, data: response, measure, status: 'job' };
  }

  if (isReportFeedbackData(response)) {
    return { compare, data: response as ReportFeedbackData, measure, status: 'result' };
  }

  return { compare, measure, status: 'missing' };
}

const FLAG_GET_REPORT_PASSIVE_ASYNC = localStorage.getItem('FLAG_GET_REPORT_PASSIVE_ASYNC');
const REPORTING_ASYNC_DISABLED = ENV.REPORTING_ASYNC_DISABLED;

export function useReportFeedbackStatic({ widgetId, widgetType }: UseReportFeedbackStaticProps) {
  const { t, i18n } = useTranslation('page-reports');
  const contentCategories = useAppSelector(selectContentCategoriesSelector);
  const jobResults = useJobResults() as DigestJobResult[] | null;
  const requestQueue = useReportDataRequestQueue();
  const requestQueueKey = `${widgetId ?? 'report-data'}:${useId()}`;
  const requestCompletionRef = useRef<(() => void) | null>(null);
  const requestBatchReadyRef = useRef(false);
  const requestGenerationRef = useRef(0);

  const passiveAsync =
    FLAG_GET_REPORT_PASSIVE_ASYNC === 'true' || (FLAG_GET_REPORT_PASSIVE_ASYNC === null && REPORTING_ASYNC_DISABLED);

  const urlParams = useMemo(() => parseReportPrintParams(), []);

  const digestData = useMemo(() => {
    return urlParams.digest ? getDigestDataFromUrl() : null;
  }, [urlParams.digest]) as DigestData | null;

  const isDigest = !!digestData;
  const hasJobResults = !!jobResults;
  const isReportEmailSharingExport = window.location.pathname.includes('/export-report') && !!urlParams.job_results_key;

  const [allData, setAllData] = useState<Array<ReportFeedbackStaticResponseItem> | null>(null);
  const [createJobId, { reset: resetCreateJobId }] = useCreateFeedbackOrJobIdMutation();
  const [activeRequestJobId, setActiveRequestJobId] = useState<string | null>(null);
  const [activeRequestSource, setActiveRequestSource] = useState<ReportDataSource>('feedback');
  const [isRequesting, setIsRequesting] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [processItems, setProcessItems] = useState<Array<ReportFeedbackStaticResponseItem>>([]);
  const [isCountOnlyRequest, setIsCountOnlyRequest] = useState(false);
  const [feedbackCount, setFeedbackCount] = useState<number>(0);

  const {
    data: responseChartStaticData,
    isFetching: isFetchingChartStaticData,
    isError: isErrorChartStaticData,
    originalArgs,
  } = useGetFeedbackStaticResultQuery(
    {
      jobId: activeRequestJobId,
      source: activeRequestSource,
    },
    {
      skip: !activeRequestJobId,
      pollingInterval: 2000,
      refetchOnFocus: false,
      skipPollingIfUnfocused: true,
      refetchOnMountOrArgChange: false,
    },
  );

  const settleRequest = useCallback(() => {
    requestCompletionRef.current?.();
    requestCompletionRef.current = null;
  }, []);

  const handleOnResetData = useCallback(() => {
    requestGenerationRef.current += 1;
    requestBatchReadyRef.current = false;
    setAllData(null);
    setIsError(false);
    setActiveRequestJobId(null);
    setIsRequesting(false);
    setIsTimedOut(false);
    setIsCountOnlyRequest(false);
    setFeedbackCount(0);
    setProcessItems([]);
    resetCreateJobId();
    settleRequest();
  }, [resetCreateJobId, settleRequest]);

  useEffect(() => {
    if (isErrorChartStaticData && requestBatchReadyRef.current) {
      requestBatchReadyRef.current = false;
      setIsError(true);
      setProcessItems([]);
      setIsRequesting(false);
      setActiveRequestJobId(null);
      settleRequest();
    }
  }, [isErrorChartStaticData, settleRequest]);

  const handleOnRequest = (values: ReportChartDataRequestItem[], async: boolean = true): Promise<void> => {
    const executeRequest = () =>
      new Promise<void>((resolve) => {
        settleRequest();
        requestCompletionRef.current = resolve;
        const requestGeneration = ++requestGenerationRef.current;

        const hasCountOnly = values.some((request) => 'count_only' in request.data && request.data.count_only === true);
        setActiveRequestSource(values[0]?.source ?? 'feedback');
        requestBatchReadyRef.current = false;
        setProcessItems([]);

        if (!hasCountOnly) {
          setAllData(null);
          setIsError(false);
          setActiveRequestJobId(null);
          setIsTimedOut(false);
          setIsCountOnlyRequest(false);
          setFeedbackCount(0);
          resetCreateJobId();
        } else {
          setFeedbackCount(0);
        }

        if (values.length === 0) {
          setIsRequesting(false);
          settleRequest();
          return;
        }

        setIsRequesting(true);
        // If widgetId is provided, async defaults to true. If not, async must be false.
        requestGraphicDataJobId(values, passiveAsync ? false : widgetId ? async : false, requestGeneration);
      });

    return requestQueue ? requestQueue.enqueue(requestQueueKey, executeRequest) : executeRequest();
  };

  const nextProcessItem = useMemo(() => {
    const nextItem = processItems.find((item) => !item.isRequested && item.jobId);
    return nextItem || null;
  }, [processItems]);

  const displayProcessItems = useMemo(() => {
    return getDisplayProcessItems(processItems);
  }, [processItems]);

  const countOnlyProcessItemsTotal = useMemo(() => {
    const countOnlyItems = processItems.filter((item) => item.isCountOnly && item.data);
    if (countOnlyItems.length === 0) return 0;
    let total = 0;
    for (const item of countOnlyItems) {
      if (item.data) total += calculateTotalFeedbackCount(item.data);
    }
    return total;
  }, [processItems]);

  useEffect(() => {
    if (!requestBatchReadyRef.current) {
      return;
    }

    if (nextProcessItem) {
      setActiveRequestJobId(nextProcessItem.jobId);
      setIsCountOnlyRequest(nextProcessItem.isCountOnly);
      return;
    }

    if (displayProcessItems.length > 0) {
      setAllData(displayProcessItems);
    }

    if (countOnlyProcessItemsTotal > 0) {
      setFeedbackCount(countOnlyProcessItemsTotal);
    }

    setActiveRequestJobId(null);
    setIsRequesting(false);
    setIsCountOnlyRequest(false);
    requestBatchReadyRef.current = false;
    setProcessItems([]);
    settleRequest();
  }, [nextProcessItem, displayProcessItems, countOnlyProcessItemsTotal, settleRequest]);

  useEffect(() => {
    if (!requestBatchReadyRef.current) {
      return;
    }

    if (responseChartStaticData && !isFetchingChartStaticData) {
      if (responseChartStaticData.status === 'completed') {
        if (isCountOnlyRequest && responseChartStaticData.result?.data) {
          const count = calculateFeedbackIncrement(responseChartStaticData.result.data, undefined, true);
          setFeedbackCount((prevCount) => prevCount + count);
        }

        // If the originalArgs?.jobId in the incoming response is equal to the incoming jobId, complete the operation.
        const items: ReportFeedbackStaticResponseItem[] = processItems.map((item) => {
          if (item.jobId === originalArgs?.jobId) {
            if (!isCountOnlyRequest && responseChartStaticData.result?.data) {
              const count = calculateFeedbackIncrement(responseChartStaticData.result.data, item.measure, false);
              if (count > 0) {
                setFeedbackCount((prevCount) => prevCount + count);
              }
            }

            if (widgetType !== 'table-chart-ata' && reportWidgetHasContentCategoryCompare(widgetType, item.compare)) {
              return {
                ...item,
                data: updateCategoryContentNames(
                  responseChartStaticData.result.data,
                  contentCategories,
                  widgetType,
                  item.compare,
                ),
                totalFeedbackCount: getTotalFeedbackCount(responseChartStaticData.result),
                isRequested: true,
              };
            }
            return {
              ...item,
              data: responseChartStaticData.result.data,
              totalFeedbackCount: getTotalFeedbackCount(responseChartStaticData.result),
              isRequested: true,
            };
          }
          return item;
        });

        setProcessItems(items);
      } else if (responseChartStaticData.status === 'failed') {
        requestBatchReadyRef.current = false;
        setProcessItems([]);
        setIsRequesting(false);
        setActiveRequestJobId(null);
        setIsError(true);
        settleRequest();
      } else if (responseChartStaticData.status === 'timed_out') {
        requestBatchReadyRef.current = false;
        setProcessItems([]);
        setIsRequesting(false);
        setActiveRequestJobId(null);
        setIsTimedOut(true);
        settleRequest();
      }
    }
  }, [responseChartStaticData, isFetchingChartStaticData, settleRequest]);

  const requestGraphicDataJobId = useDebouncedCallback(
    async (chartRequests: ReportChartDataRequestItem[], async: boolean, requestGeneration: number) => {
      try {
        const hasCountOnly = chartRequests.some(
          (request) => 'count_only' in request.data && request.data.count_only === true,
        );
        setIsCountOnlyRequest(hasCountOnly);

        // If passiveAsync is true then async is returned as false.
        // First get the job id to start all the processes
        const responses: ChartRequestResponse[] = [];

        for (const { url, data, measure, compare, source = 'feedback' } of chartRequests) {
          const response = await (async () => {
            if (source === 'feedback' && hasJobResults && jobResults && widgetId) {
              const cachedResponse = getCachedChartResponse({
                cachedResults: jobResults,
                widgetId,
                url,
                measure,
                compare,
                data,
                requireResult: isReportEmailSharingExport,
              });

              if (cachedResponse.status !== 'missing') {
                return cachedResponse;
              }

              if (isReportEmailSharingExport) {
                return cachedResponse;
              }
            } else if (source === 'feedback' && isDigest && digestData) {
              const cachedResponse = getCachedChartResponse({
                cachedResults: digestData.job_results,
                widgetId,
                url,
                measure,
                compare,
                data,
                requireResult: false,
              });

              if (cachedResponse.status !== 'missing') {
                return cachedResponse;
              }

              return cachedResponse;
            }

            return createJobId({ params: data, url, widgetId, async, source })
              .unwrap()
              .then((response) => {
                return toChartRequestResponse({ compare, measure, response });
              })
              .finally(() => {
                if (requestGeneration === requestGenerationRef.current) {
                  resetCreateJobId();
                }
              })
              .catch((error) => {
                console.error('error', error);
                throw error;
              });
          })();

          if (requestGeneration !== requestGenerationRef.current) {
            return;
          }

          responses.push(response);
        }

        if (requestGeneration !== requestGenerationRef.current) {
          return;
        }

        const hasMissingCachedResponse = responses.some((response) => response.status === 'missing');

        if (isReportEmailSharingExport && hasMissingCachedResponse) {
          requestBatchReadyRef.current = false;
          setProcessItems([]);
          setIsRequesting(false);
          setActiveRequestJobId(null);
          setIsError(true);
          settleRequest();
          return;
        }

        // @TodoRepoting: If have a error in the responses, handle it.
        const items: ReportFeedbackStaticResponseItem[] = [];
        let totalCount = 0;

        // get the job id of the processes after they start
        for (let i = 0; i < responses.length; i++) {
          const response = responses[i];
          const requestData = chartRequests[i]?.data;
          const isCountOnly = requestData && 'count_only' in requestData && requestData.count_only === true;

          if (response.status === 'job') {
            const jobId = response.data.job_id;
            items.push({ measure: response.measure, jobId, data: null, compare: response.compare, isCountOnly });
          } else if (response.status === 'result') {
            const count = calculateFeedbackIncrement(response.data.data, response.measure, hasCountOnly);
            totalCount += count;

            // Dont trust backend check response.data.data
            // if direct return data add data but jobId null
            // because requests will be made continuously for the values received that have a job id and no data.

            if (
              widgetType !== 'table-chart-ata' &&
              reportWidgetHasContentCategoryCompare(widgetType, response.compare)
            ) {
              items.push({
                measure: response.measure,
                jobId: null,
                data: updateCategoryContentNames(response.data.data, contentCategories, widgetType, response.compare),
                totalFeedbackCount: getTotalFeedbackCount(response.data),
                compare: response.compare,
                isCountOnly,
              });
            } else {
              items.push({
                measure: response.measure,
                jobId: null,
                data: response.data.data,
                totalFeedbackCount: getTotalFeedbackCount(response.data),
                compare: response.compare,
                isCountOnly,
              });
            }
          }
        }

        if (hasCountOnly || totalCount > 0) {
          setFeedbackCount(totalCount);
        }

        if (isReportEmailSharingExport && chartRequests.length > 0 && items.length === 0) {
          requestBatchReadyRef.current = false;
          setProcessItems([]);
          setIsRequesting(false);
          setActiveRequestJobId(null);
          setIsError(true);
          settleRequest();
          return;
        }

        requestBatchReadyRef.current = true;
        setProcessItems(items);
      } catch (error) {
        if (requestGeneration !== requestGenerationRef.current) {
          return;
        }

        console.error('error', error);
        requestBatchReadyRef.current = false;
        setProcessItems([]);
        setIsRequesting(false);
        setActiveRequestJobId(null);
        setIsError(true);
        settleRequest();
      }
    },
    1000,
  );

  useEffect(() => {
    return () => {
      requestGenerationRef.current += 1;
      requestBatchReadyRef.current = false;
      settleRequest();
    };
  }, [settleRequest]);

  const localizedData = useMemo(
    () => localizeTicketMeasureNames(allData, t),
    [allData, t, i18n.language, i18n.resolvedLanguage],
  );

  return {
    allData: localizedData,
    handleOnResetData,
    handleOnRequest,
    isRequesting,
    isError: isErrorChartStaticData || isError,
    isTimedOut,
    feedbackCount,
  };
}
